import React, {useEffect, useMemo, useRef, useState} from 'react';
import BreadcrumbsBar from '../../layouts/breadcrumb/BreadcrumbsBar';
import Card from '../../common/card/Card';
import {Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment, List, ListItemButton, ListItemText, Slider, TextField, Typography} from '@mui/material';
import styles from './CreateHtmlTemplate.module.scss';
import Toast from '../../common/toast/Toast';
import useToast from '../../common/hooks/useToast';
import {EditorContent, useEditor} from '@tiptap/react';
import {mergeAttributes} from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TiptapToolbar from './components/TiptapToolbar';
import ImageUploadModal from './components/ImageUploadModal';
import {getCustomEditorIconLibrary, getCustomPageDataByLink, getCustomPages, insertNewPageType, updateCustomPage} from '../../services/createCustomHtmlService';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DEFAULT_EDITOR_CONTENT = {
    type: 'doc',
    content: [{
        type: 'paragraph',
        content: [{type: 'text', text: 'Start writing your page content here...'}]
    }]
};

const parseEditorContent = (rawContent) => {
    if (!rawContent) return DEFAULT_EDITOR_CONTENT;

    if (typeof rawContent === 'string') {
        try {
            const parsedContent = JSON.parse(rawContent);
            return parsedContent?.type ? parsedContent : DEFAULT_EDITOR_CONTENT;
        } catch {
            return DEFAULT_EDITOR_CONTENT;
        }
    }

    return rawContent?.type ? rawContent : DEFAULT_EDITOR_CONTENT;
};

const toSlug = (value) => value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s/-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\/+/g, '/')
    .replace(/^\/+|\/+$/g, '');

const normalizePage = (page) => ({
    ...page,
    id: page?.id || page?._id,
    link: page?.link || page?.page_link || '',
    status: page?.status || 'published'
});

const getDisplayStatus = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus === 'archived') return 'ARCHIVED';
    return 'PUBLISHED';
};

const normalizeResponseArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data.map(normalizePage);
    if (Array.isArray(data?.rows)) return data.rows.map(normalizePage);
    if (Array.isArray(data?.data)) return data.data.map(normalizePage);
    return [];
};

const normalizeSinglePage = (data) => {
    if (!data) return null;
    if (Array.isArray(data)) return normalizePage(data[0] || {});
    if (Array.isArray(data?.rows)) return normalizePage(data.rows[0] || {});
    if (Array.isArray(data?.data)) return normalizePage(data.data[0] || {});
    return normalizePage(data);
};

const normalizeIconItems = (data) => {
    const rawItems = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
            ? data.rows
            : Array.isArray(data?.data)
                ? data.data
                : [];

    return rawItems
        .map((item) => {
            if (typeof item === 'string') {
                return {url: item, name: item.split('/').pop() || 'icon'};
            }

            return {
                url: item?.url || item?.publicUrl || item?.image_url || '',
                name: item?.name || item?.filename || item?.title || (item?.url ? item.url.split('/').pop() : 'icon')
            };
        })
        .filter((item) => Boolean(item.url));
};

const getImageStyle = ({width, align}) => {
    const safeWidth = width || '100%';
    const safeAlign = align || 'center';
    const baseStyle = `width:${safeWidth};max-width:100%;height:auto;display:block;`;

    if (safeAlign === 'left') return `${baseStyle}margin-left:0;margin-right:auto;`;
    if (safeAlign === 'right') return `${baseStyle}margin-left:auto;margin-right:0;`;
    return `${baseStyle}margin-left:auto;margin-right:auto;`;
};

const ResizableImage = Image.extend({
    addAttributes() {
        const parentAttributes = this.parent?.() || {};

        return {
            ...parentAttributes,
            width: {
                default: '100%',
                parseHTML: (element) => element.style.width || element.getAttribute('width') || '100%',
                renderHTML: (attributes) => {
                    return {
                        style: getImageStyle(attributes)
                    };
                }
            },
            align: {
                default: 'center',
                parseHTML: (element) => element.getAttribute('data-align') || 'center',
                renderHTML: (attributes) => ({
                    'data-align': attributes.align || 'center'
                })
            },
            href: {
                default: null,
                parseHTML: (element) => (
                    element.parentElement?.tagName === 'A'
                        ? element.parentElement.getAttribute('href')
                        : null
                ),
                renderHTML: () => ({})
            },
            target: {
                default: '_blank',
                parseHTML: (element) => (
                    element.parentElement?.tagName === 'A'
                        ? (element.parentElement.getAttribute('target') || '_blank')
                        : '_blank'
                ),
                renderHTML: () => ({})
            },
            rel: {
                default: 'noopener noreferrer',
                parseHTML: (element) => (
                    element.parentElement?.tagName === 'A'
                        ? (element.parentElement.getAttribute('rel') || 'noopener noreferrer')
                        : 'noopener noreferrer'
                ),
                renderHTML: () => ({})
            }
        };
    },
    renderHTML({HTMLAttributes, node}) {
        const nodeAttributes = node?.attrs || {};
        const href = nodeAttributes.href || HTMLAttributes.href;
        const target = nodeAttributes.target || HTMLAttributes.target;
        const rel = nodeAttributes.rel || HTMLAttributes.rel;
        const {href: _href, target: _target, rel: _rel, ...imgAttributes} = HTMLAttributes;
        const imageTag = ['img', mergeAttributes(this.options.HTMLAttributes, imgAttributes)];

        if (href) {
            return ['a', {href, target: target || '_blank', rel: rel || 'noopener noreferrer'}, imageTag];
        }

        return imageTag;
    }
});

const normalizeUrl = (value) => {
    const trimmedValue = String(value || '').trim();
    if (!trimmedValue) return '';

    if (/^https?:\/\//i.test(trimmedValue) || /^mailto:/i.test(trimmedValue) || /^tel:/i.test(trimmedValue)) {
        return trimmedValue;
    }

    return `https://${trimmedValue}`;
};

const CreateHtmlTemplate = () => {
    const [newPageLink, setNewPageLink] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [allPages, setAllPages] = useState([]);
    const [selectedPageLink, setSelectedPageLink] = useState(null);
    const [editedPage, setEditedPage] = useState(null);
    const [isImageUploadOpen, setIsImageUploadOpen] = useState(false);
    const [isIconLibraryOpen, setIsIconLibraryOpen] = useState(false);
    const [iconLibraryItems, setIconLibraryItems] = useState([]);
    const [iconSearch, setIconSearch] = useState('');
    const [isIconLibraryLoading, setIsIconLibraryLoading] = useState(false);
    const [iconLibraryError, setIconLibraryError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isImageSelected, setIsImageSelected] = useState(false);
    const [selectedImageWidth, setSelectedImageWidth] = useState(100);
    const [selectedImageAlign, setSelectedImageAlign] = useState('center');
    const [selectedImageHref, setSelectedImageHref] = useState('');
    const lastSelectionRef = useRef(null);
    const uploadSlotTokenRef = useRef(null);
    const hydratedPageKeyRef = useRef(null);
    const {isToastOpen, toggleToast, toastContent, setToastContent} = useToast();

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
                protocols: ['http', 'https']
            }),
            ResizableImage.configure({
                allowBase64: false,
                inline: false
            }),
            Placeholder.configure({
                placeholder: 'Craft an elegant page with headings, visuals, links, and strong CTA blocks.'
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph']
            })
        ],
        content: DEFAULT_EDITOR_CONTENT,
        onUpdate: ({editor: currentEditor}) => {
            const jsonContent = currentEditor.getJSON();
            setEditedPage((prevState) => {
                if (!prevState) return prevState;
                return {
                    ...prevState,
                    content_json: jsonContent
                };
            });
        }
    });

    const filteredPages = useMemo(() => {
        const normalizedSearch = searchQuery.trim().toLowerCase();
        if (!normalizedSearch) return allPages;

        return allPages.filter((page) => (page.link || '').toLowerCase().includes(normalizedSearch));
    }, [allPages, searchQuery]);

    const filteredIconLibraryItems = useMemo(() => {
        const query = iconSearch.trim().toLowerCase();
        if (!query) return iconLibraryItems;

        return iconLibraryItems.filter((item) => (item.name || '').toLowerCase().includes(query));
    }, [iconLibraryItems, iconSearch]);

    useEffect(() => {
        if (!editor) return;

        const syncSelectedImageState = () => {
            const selectedNode = editor.state.selection.node;
            const selectedIsImage = selectedNode?.type?.name === 'image';

            setIsImageSelected(selectedIsImage);

            if (selectedIsImage) {
                const rawWidth = selectedNode?.attrs?.width || '100%';
                const parsedWidth = parseInt(String(rawWidth).replace('%', ''), 10);
                const safeWidth = Number.isFinite(parsedWidth) ? Math.min(100, Math.max(10, parsedWidth)) : 100;
                setSelectedImageWidth(safeWidth);
                setSelectedImageAlign(selectedNode?.attrs?.align || 'center');
                setSelectedImageHref(selectedNode?.attrs?.href || '');
            } else {
                setSelectedImageHref('');
            }
        };

        syncSelectedImageState();
        editor.on('selectionUpdate', syncSelectedImageState);
        editor.on('update', syncSelectedImageState);

        return () => {
            editor.off('selectionUpdate', syncSelectedImageState);
            editor.off('update', syncSelectedImageState);
        };
    }, [editor]);

    const getAllPages = async () => {
        const response = await getCustomPages();
        const pages = normalizeResponseArray(response?.data)
            .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
        setAllPages(pages);

        if (!selectedPageLink && pages[0]) {
            setSelectedPageLink(pages[0].link);
        }
    };

    useEffect(() => {
        getAllPages();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const loadSelectedPage = async () => {
            if (!selectedPageLink) {
                setEditedPage(null);
                return;
            }

            const response = await getCustomPageDataByLink(selectedPageLink);
            const detailedPage = normalizeSinglePage(response?.data);

            if (detailedPage?.link) {
                setEditedPage(detailedPage);
                return;
            }

            const fallbackPage = allPages.find((page) => page.link === selectedPageLink) || null;
            setEditedPage(fallbackPage);
        };

        loadSelectedPage();
    }, [allPages, selectedPageLink]);

    const editedPageId = editedPage?.id || editedPage?._id || '';
    const editedPageLink = editedPage?.link || '';
    const editedPageUpdatedAt = editedPage?.updated_at || '';
    const editedPageContentJson = editedPage?.content_json;

    useEffect(() => {
        if (!editor || !editedPageId) return;

        const pageKey = `${editedPageId}:${editedPageLink}:${editedPageUpdatedAt}`;
        if (hydratedPageKeyRef.current === pageKey) return;

        const parsedContent = parseEditorContent(editedPageContentJson);
        editor.commands.setContent(parsedContent, false);
        hydratedPageKeyRef.current = pageKey;
    }, [editor, editedPageId, editedPageLink, editedPageUpdatedAt, editedPageContentJson]);

    const isLinkExist = (link) => allPages.some((page) => page.link === link);

    const createPage = async () => {
        const sanitizedLink = toSlug(newPageLink);

        if (!sanitizedLink) {
            setToastContent('Page link is required.');
            toggleToast();
            return;
        }

        if (isLinkExist(sanitizedLink)) {
            setToastContent('This page link already exists.');
            toggleToast();
            return;
        }

        const payload = {
            link: sanitizedLink,
            content_json: DEFAULT_EDITOR_CONTENT,
            status: 'published'
        };

        const response = await insertNewPageType(payload);
        const createdPage = normalizePage(response?.data || {});

        if (response?.data) {
            setToastContent('Page created successfully');
            setNewPageLink('');
            await getAllPages();
            setSelectedPageLink(createdPage.link || sanitizedLink);
        } else {
            setToastContent('Error creating page');
        }

        toggleToast();
    };

    const handleUpdatePage = async () => {
        if (!editedPage) return;

        setIsSaving(true);

        try {
            const payload = {
                id: editedPage.id || editedPage._id,
                link: toSlug(editedPage.link || ''),
                status: editedPage.status || 'published',
                content_json: editor?.getJSON() || parseEditorContent(editedPage.content_json)
            };

            const response = await updateCustomPage(payload);

            if (response?.data) {
                setToastContent('Page updated successfully');
                await getAllPages();
            } else {
                setToastContent('Error updating page');
            }

            toggleToast();
        } finally {
            setIsSaving(false);
        }
    };

    const buildImageNodes = (urls) => urls.flatMap((url) => ([
        {type: 'image', attrs: {src: url, alt: 'Uploaded image', width: '100%', align: 'center', href: null}},
        {type: 'paragraph'}
    ]));

    const findUploadTokenRange = (token) => {
        if (!editor || !token) return null;
        let foundRange = null;

        editor.state.doc.descendants((node, pos) => {
            if (!node.isText || !node.text) return true;
            const index = node.text.indexOf(token);
            if (index === -1) return true;

            foundRange = {
                from: pos + index,
                to: pos + index + token.length
            };
            return false;
        });

        return foundRange;
    };

    const removeUploadSlotIfExists = () => {
        const token = uploadSlotTokenRef.current;
        const tokenRange = findUploadTokenRange(token);

        if (tokenRange) {
            editor
                ?.chain()
                .focus()
                .insertContentAt({from: tokenRange.from, to: tokenRange.to}, '')
                .run();
        }

        uploadSlotTokenRef.current = null;
    };

    const handleImagesUploaded = (imageUrls) => {
        if (!editor || !Array.isArray(imageUrls) || imageUrls.length === 0) return;

        const imageNodes = buildImageNodes(imageUrls);
        const token = uploadSlotTokenRef.current;
        const tokenRange = findUploadTokenRange(token);

        if (tokenRange) {
            editor
                .chain()
                .focus()
                .insertContentAt({from: tokenRange.from, to: tokenRange.to}, imageNodes)
                .focus('end')
                .run();
            uploadSlotTokenRef.current = null;
            return;
        }

        const savedSelection = lastSelectionRef.current;
        if (savedSelection?.from !== undefined && savedSelection?.to !== undefined) {
            editor
                .chain()
                .focus()
                .setTextSelection({from: savedSelection.from, to: savedSelection.to})
                .insertContent(imageNodes)
                .focus('end')
                .run();
            return;
        }

        editor.chain().focus().insertContent(imageNodes).focus('end').run();
    };

    const openIconLibrary = async () => {
        if (!editor) return;

        const {from, to} = editor.state.selection;
        lastSelectionRef.current = {from, to};
        setIsIconLibraryOpen(true);

        if (iconLibraryItems.length > 0) return;

        setIsIconLibraryLoading(true);
        setIconLibraryError('');

        try {
            const response = await getCustomEditorIconLibrary();
            const normalizedItems = normalizeIconItems(response?.data);
            setIconLibraryItems(normalizedItems);
        } catch (error) {
            setIconLibraryError(error?.message || 'Failed to load icon library');
        } finally {
            setIsIconLibraryLoading(false);
        }
    };

    const insertLibraryIcon = (iconUrl) => {
        if (!editor || !iconUrl) return;

        const savedSelection = lastSelectionRef.current;
        const iconNode = {type: 'image', attrs: {src: iconUrl, alt: 'Library icon', width: '20%', align: 'left', href: null}};

        if (savedSelection?.from !== undefined && savedSelection?.to !== undefined) {
            editor
                .chain()
                .focus()
                .setTextSelection({from: savedSelection.from, to: savedSelection.to})
                .insertContent(iconNode)
                .focus()
                .run();
        } else {
            editor.chain().focus().insertContent(iconNode).focus().run();
        }

        setIsIconLibraryOpen(false);
    };

    const applySelectedImageWidth = (widthValue) => {
        if (!editor) return;

        const clampedValue = Math.min(100, Math.max(10, widthValue));
        editor.chain().focus().updateAttributes('image', {
            width: `${clampedValue}%`,
            align: selectedImageAlign,
            href: selectedImageHref || null
        }).run();
        setSelectedImageWidth(clampedValue);
    };

    const applySelectedImageAlign = (alignValue) => {
        if (!editor) return;

        const nextAlign = ['left', 'center', 'right'].includes(alignValue) ? alignValue : 'center';
        editor.chain().focus().updateAttributes('image', {
            width: `${selectedImageWidth}%`,
            align: nextAlign,
            href: selectedImageHref || null
        }).run();
        setSelectedImageAlign(nextAlign);
    };

    const applySelectedImageLink = () => {
        if (!editor || !isImageSelected) return;
        const normalizedHref = normalizeUrl(selectedImageHref);
        setSelectedImageHref(normalizedHref);
        editor.chain().focus().updateAttributes('image', {
            href: normalizedHref || null,
            target: '_blank',
            rel: 'noopener noreferrer'
        }).run();
    };

    const clearSelectedImageLink = () => {
        if (!editor || !isImageSelected) return;
        setSelectedImageHref('');
        editor.chain().focus().updateAttributes('image', {
            href: null,
            target: '_blank',
            rel: 'noopener noreferrer'
        }).run();
    };

    const removeSelectedImage = () => {
        if (!editor || !isImageSelected) return;
        editor.chain().focus().deleteSelection().run();
        setIsImageSelected(false);
    };

    return (
        <div>
            <BreadcrumbsBar/>
            <Card title="Static Pages Editor">
                <div className={`${styles.layout} ${isSidebarCollapsed ? styles.layoutCollapsed : ''}`}>
                    <aside className={`${styles.sidebar} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
                        <IconButton
                            size="small"
                            className={styles.sidebarToggle}
                            onClick={() => setIsSidebarCollapsed((prevState) => !prevState)}
                        >
                            {isSidebarCollapsed ? <ChevronRightIcon fontSize="small"/> : <ChevronLeftIcon fontSize="small"/>}
                        </IconButton>

                        {!isSidebarCollapsed && (
                            <>
                        <Box className={styles.sidebarHead}>
                            <Typography variant="h6">Pages</Typography>
                            <Chip
                                size="small"
                                label={`${allPages.length} total`}
                                variant="outlined"
                                className={styles.countChip}
                            />
                        </Box>
                        <TextField
                            label="Search page"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            fullWidth
                            size="small"
                            className={styles.searchInput}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small"/>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <List className={styles.pageList}>
                            {filteredPages.map((page) => {
                                const pageKey = page.link;
                                const isSelected = page.link === selectedPageLink;

                                return (
                                    <ListItemButton
                                        key={pageKey}
                                        selected={isSelected}
                                        onClick={() => setSelectedPageLink(page.link)}
                                        className={styles.pageListItem}
                                    >
                                        <ListItemText
                                            primary={`/${page.link}`}
                                            secondary={
                                                <span className={styles.rowMeta}>
                                                    <span className={styles.rowStatus}>
                                                        {getDisplayStatus(page.status)}
                                                    </span>
                                                    {page.updated_at && (
                                                        <span className={styles.rowDate}>
                                                            {new Date(page.updated_at).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </span>
                                            }
                                            primaryTypographyProps={{fontWeight: isSelected ? 700 : 500}}
                                        />
                                    </ListItemButton>
                                );
                            })}

                            {filteredPages.length === 0 && (
                                <Box className={styles.noPages}>
                                    <Typography variant="body2">No pages found for this search.</Typography>
                                </Box>
                            )}
                        </List>

                        <Divider/>

                        <Typography variant="subtitle1" className={styles.createTitle}>Create New Page</Typography>
                        <TextField
                            label="Page Link"
                            value={newPageLink}
                            onChange={(event) => setNewPageLink(toSlug(event.target.value))}
                            helperText="Only this is mandatory. Example: spring-campaign"
                            fullWidth
                            size="small"
                            className={styles.createInput}
                        />
                        <Box className={styles.linkPreviewCard}>
                            <Typography variant="caption" className={styles.linkPreviewLabel}>
                                Live preview
                            </Typography>
                            <Typography variant="body2" className={styles.linkPreviewLive}>
                                {newPageLink
                                    ? `http://localhost:3000/cp?l=${newPageLink}`
                                    : 'http://localhost:3000/cp?l=your-page-link'}
                            </Typography>
                        </Box>
                        <Button variant="contained" color="info" onClick={createPage} className={styles.createBtn}>
                            Create Page
                        </Button>
                            </>
                        )}
                    </aside>

                    <main className={styles.editorPanel}>
                        {!editedPage && (
                            <Box className={styles.emptyState}>
                                <Typography variant="h6">Create or select a page from the left</Typography>
                                <Typography variant="body2">Build the entire page directly in Tiptap and publish via link.</Typography>
                            </Box>
                        )}

                        {editedPage && (
                            <>
                                <Box className={styles.editorHeader}>
                                    <Typography variant="h6">{`Editing /${editedPage.link}`}</Typography>
                                    <Box className={styles.headerActions}>
                                        <Chip label={getDisplayStatus(editedPage.status)} variant="outlined"/>
                                        <Button variant="outlined" href={`/cp?l=${editedPage.link}`} target="_blank">
                                            Preview Live
                                        </Button>
                                    </Box>
                                </Box>

                                <TextField
                                    label="Page Link"
                                    value={editedPage.link || ''}
                                    onChange={(event) => {
                                        const value = toSlug(event.target.value);
                                        setEditedPage((prevState) => ({...prevState, link: value}));
                                    }}
                                    fullWidth
                                    size="small"
                                />

                                <Box className={styles.editorCard}>
                                    <Box className={styles.editorStickyControls}>
                                        <TiptapToolbar
                                            editor={editor}
                                            onOpenImageUpload={() => {
                                                if (editor) {
                                                    const {from, to} = editor.state.selection;
                                                    lastSelectionRef.current = {from, to};
                                                    const slotToken = `[Uploading images...${Date.now()}]`;
                                                    uploadSlotTokenRef.current = slotToken;
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .setTextSelection({from, to})
                                                        .insertContent({
                                                            type: 'paragraph',
                                                            content: [{type: 'text', text: slotToken}]
                                                        })
                                                        .run();
                                                }
                                                setIsImageUploadOpen(true);
                                            }}
                                            onOpenIconLibrary={openIconLibrary}
                                        />
                                        {isImageSelected && (
                                            <Box className={styles.imageResizeBar}>
                                                <Box className={styles.imageBarTop}>
                                                    <Typography variant="subtitle2" className={styles.imageBarTitle}>
                                                        Image Controls
                                                    </Typography>
                                                    <Typography variant="body2" className={styles.imageBarValue}>
                                                        Width {selectedImageWidth}%
                                                    </Typography>
                                                </Box>

                                                <Slider
                                                    size="small"
                                                    min={10}
                                                    max={100}
                                                    step={5}
                                                    className={styles.imageWidthSlider}
                                                    value={selectedImageWidth}
                                                    onChange={(event, value) => {
                                                        const nextValue = Array.isArray(value) ? value[0] : value;
                                                        setSelectedImageWidth(nextValue);
                                                    }}
                                                    onChangeCommitted={(event, value) => {
                                                        const nextValue = Array.isArray(value) ? value[0] : value;
                                                        applySelectedImageWidth(nextValue);
                                                    }}
                                                />

                                                <Box className={styles.imageActionGroup}>
                                                    <Typography variant="caption" className={styles.imageGroupLabel}>Quick size</Typography>
                                                    <Box className={styles.imageResizeActions}>
                                                        <Button size="small" className={styles.quickActionBtn} onClick={() => applySelectedImageWidth(33)}>33%</Button>
                                                        <Button size="small" className={styles.quickActionBtn} onClick={() => applySelectedImageWidth(50)}>50%</Button>
                                                        <Button size="small" className={styles.quickActionBtn} onClick={() => applySelectedImageWidth(75)}>75%</Button>
                                                        <Button size="small" className={styles.quickActionBtn} onClick={() => applySelectedImageWidth(100)}>100%</Button>
                                                    </Box>
                                                </Box>

                                                <Box className={styles.imageActionGroup}>
                                                    <Typography variant="caption" className={styles.imageGroupLabel}>Alignment & actions</Typography>
                                                    <Box className={styles.imageResizeActions}>
                                                        <Button
                                                            size="small"
                                                            className={styles.alignActionBtn}
                                                            variant={selectedImageAlign === 'left' ? 'contained' : 'outlined'}
                                                            onClick={() => applySelectedImageAlign('left')}
                                                        >
                                                            Left
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            className={styles.alignActionBtn}
                                                            variant={selectedImageAlign === 'center' ? 'contained' : 'outlined'}
                                                            onClick={() => applySelectedImageAlign('center')}
                                                        >
                                                            Center
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            className={styles.alignActionBtn}
                                                            variant={selectedImageAlign === 'right' ? 'contained' : 'outlined'}
                                                            onClick={() => applySelectedImageAlign('right')}
                                                        >
                                                            Right
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            className={styles.removeImageBtn}
                                                            variant="outlined"
                                                            onClick={removeSelectedImage}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                </Box>

                                                <Box className={styles.imageActionGroup}>
                                                    <Typography variant="caption" className={styles.imageGroupLabel}>Click action</Typography>
                                                    <Box className={styles.imageResizeActions}>
                                                        <TextField
                                                            size="small"
                                                            label="Image link URL"
                                                            value={selectedImageHref}
                                                            onChange={(event) => setSelectedImageHref(event.target.value)}
                                                            className={styles.imageLinkInput}
                                                        />
                                                        <Button size="small" className={styles.linkApplyBtn} variant="contained" onClick={applySelectedImageLink}>
                                                            Apply Link
                                                        </Button>
                                                        <Button size="small" className={styles.linkRemoveBtn} variant="outlined" onClick={clearSelectedImageLink}>
                                                            Remove Link
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                        <ImageUploadModal
                                            open={isImageUploadOpen}
                                            onClose={() => {
                                                setIsImageUploadOpen(false);
                                                removeUploadSlotIfExists();
                                            }}
                                            onImagesUploaded={handleImagesUploaded}
                                        />
                                        {isIconLibraryOpen && (
                                            <Box className={styles.iconLibraryPanel}>
                                                <Box className={styles.iconLibraryHeader}>
                                                    <Typography variant="subtitle1">Image Library</Typography>
                                                    <Button size="small" onClick={() => setIsIconLibraryOpen(false)}>Close</Button>
                                                </Box>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Search icon"
                                                    value={iconSearch}
                                                    onChange={(event) => setIconSearch(event.target.value)}
                                                />
                                                {isIconLibraryLoading && (
                                                    <Box className={styles.iconLoading}>
                                                        <CircularProgress size={18}/>
                                                        <Typography variant="body2">Loading icons...</Typography>
                                                    </Box>
                                                )}
                                                {!isIconLibraryLoading && iconLibraryError && (
                                                    <Typography variant="body2" className={styles.iconError}>
                                                        {iconLibraryError}
                                                    </Typography>
                                                )}
                                                {!isIconLibraryLoading && !iconLibraryError && (
                                                    <Box className={styles.iconGrid}>
                                                        {filteredIconLibraryItems.map((item) => (
                                                            <button
                                                                key={`${item.url}-${item.name}`}
                                                                type="button"
                                                                className={styles.iconCard}
                                                                onClick={() => insertLibraryIcon(item.url)}
                                                                title={item.name}
                                                            >
                                                                <img src={item.url} alt={item.name}/>
                                                                <span>{item.name}</span>
                                                            </button>
                                                        ))}
                                                        {filteredIconLibraryItems.length === 0 && (
                                                            <Box className={styles.iconEmpty}>
                                                                <Typography variant="body2">No icons found.</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                    <EditorContent editor={editor} className={styles.editorSurface}/>
                                </Box>

                                <Box className={styles.saveRow}>
                                    <Button
                                        variant="contained"
                                        color="info"
                                        onClick={handleUpdatePage}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving...' : 'Save'}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </main>
                </div>
            </Card>

            <Toast
                isOpen={isToastOpen}
                onClose={toggleToast}
                content={toastContent}
                severity="info"
            />
        </div>
    );
};

export default CreateHtmlTemplate;
